<script setup lang="ts">
  import {
    useGetAgreements,
    useGetMetersByBillingAccountId,
  } from "@/api/account/account"
  import { useGetPaymentScheduleDetailsV2 } from "@/api/payments/payments"
  import { aggregateQueries } from "@/lib/aggregateQueries"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import dayjs from "dayjs"
  import { computed, ref } from "vue"
  import Badge from "@/components/ui/badge/Badge.vue"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import { DialogTrigger } from "@/components/ui/dialog"
  import { Divider } from "@/components/ui/divider"
  import Icon from "@/components/ui/icons/Icon.vue"
  import Stepper from "@/components/ui/stepper/Stepper.vue"

  import SwitchCancellationModal from "@/views/myaccount/home/components/switch-cancellation-modal/SwitchCancellationModal.vue"

  import {
    formatDate,
    getConciseMeterStatusObject,
    getDropdownContent,
    getMeterSteps,
    getSwitchCancellationDeadline,
    MeterPointDisplay,
  } from "./switchProgressHelper"
  import { useSwitchProgress } from "./useSwitchProgress"

  const accountIdQuery = useGetAccountId()
  const metersQuery = useGetMetersByBillingAccountId(accountIdQuery.accountId)
  const accountId = accountIdQuery.accountId
  const agreementsQuery = useGetAgreements(accountId)
  const paymentScheduleDetailsQuery = useGetPaymentScheduleDetailsV2(accountId)
  const paymentSchedule = paymentScheduleDetailsQuery.data

  const { isLoading } = aggregateQueries([
    accountIdQuery,
    metersQuery,
    agreementsQuery,
    paymentScheduleDetailsQuery,
  ])

  const switchCancellationDeadline = computed(() =>
    getSwitchCancellationDeadline(metersQuery.data.value),
  )

  const isBeforeSwitchCancellationDeadline = computed(() =>
    switchCancellationDeadline.value?.isAfter(dayjs()),
  )

  const adaptedMeterPoints = computed(() => {
    const meterPoints = metersQuery.data.value

    const adaptedMeterPointList: MeterPointDisplay[] = []

    for (const meterPoint of meterPoints || []) {
      const conciseMeterStatus = getConciseMeterStatusObject(meterPoint)
      adaptedMeterPointList.push({
        meterPointId: conciseMeterStatus.meterPointId,
        fuelCapitalized: conciseMeterStatus.fuelCapitalized,
        fuelLowercase: conciseMeterStatus.fuelLowercase,
        iconStroke: conciseMeterStatus.iconStroke,
        badgeText: conciseMeterStatus.badgeText,
        badgeVariant: conciseMeterStatus.badgeVariant,
        meterStatusText: conciseMeterStatus.meterStatusText,
      })
    }

    return adaptedMeterPointList
  })

  const steps = computed(() => getMeterSteps(metersQuery.data.value))

  const dropDownContent = computed(() => {
    return getDropdownContent(
      paymentSchedule.value,
      agreementsQuery.data.value,
      metersQuery.data.value,
    )
  })

  const dropdownOpen = ref(false)
  const { hasCancelledSwitch, isSwitchInProgress } = useSwitchProgress()
</script>

<template>
  <Card
    v-if="!isLoading && isSwitchInProgress"
    data-testid="switch-progress-card"
  >
    <CardHeader>
      <CardTitle>Your Switch Status</CardTitle>
    </CardHeader>
    <CardContent>
      <Stepper :steps="steps" :step-count="steps.length" class="pb-4 pt-3" />
      <p
        v-if="switchCancellationDeadline && isBeforeSwitchCancellationDeadline"
        class="my-4"
      >
        Your switch has begun. You have until
        {{ formatDate(switchCancellationDeadline) }} to
        <SwitchCancellationModal @switch-cancelled="hasCancelledSwitch = true">
          <DialogTrigger class="link"> cancel the switch </DialogTrigger>
        </SwitchCancellationModal>
        if you change your mind.
      </p>

      <div class="flex flex-col gap-4">
        <div
          v-for="meter in adaptedMeterPoints"
          :key="meter.meterPointId"
          :data-testid="`meter-point-${meter.meterPointId}-status`"
        >
          <div class="grid grid-cols-2 grid-rows-2 gap-y-2 sm:flex sm:gap-x-8">
            <div class="col-span-1 flex w-[109px] flex-row">
              <div class="flex flex-row items-center">
                <Icon
                  :name="meter.fuelCapitalized"
                  size="lg"
                  :class="meter.iconStroke"
                  class="pr-1"
                />
                <p>{{ meter.fuelCapitalized }}</p>
              </div>
            </div>
            <div class="flex w-full items-center justify-center sm:w-[160px]">
              <Badge class="h-fit w-fit" :variant="meter.badgeVariant">
                {{ meter.badgeText }}
              </Badge>
            </div>
            <p class="col-span-2" data-testid="meter-status-text">
              {{ meter.meterStatusText }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="dropdownOpen">
        <Divider orientation="horizontal" class="mb-3 mt-4" />
        <div
          class="flex flex-col gap-y-3 sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-3"
          data-testid="more-details-content"
        >
          <div
            v-for="(element, index) in dropDownContent"
            :key="index"
            class="flex justify-between"
          >
            <p class="mr-4 text-nowrap sm:mr-0">{{ element.description }}</p>
            <p class="text-right font-medium">{{ element.content }}</p>
          </div>
        </div>
      </div>
    </CardContent>
    <CardFooter>
      <Button
        variant="tonal"
        icon-position="right"
        data-testid="more-details-btn"
        :icon="dropdownOpen ? 'ChevronUp' : 'ChevronDown'"
        @click="dropdownOpen = !dropdownOpen"
      >
        More Details
      </Button>
    </CardFooter>
  </Card>
</template>
